// @ts-nocheck
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const COLOUR = {
  start: '#64748b',
  end: '#0f172a',
  addback: '#22c55e',
  cut: '#ef4444',
  neutral: '#94a3b8',
};

/**
 * Build the waterfall step list from the bootstrap.
 * Each step has [base, value] so Recharts can stack the floating bars
 * (transparent base + visible value).
 */
function buildSteps(bootstrap, includeAll) {
  const start = Number(bootstrap?.summary?.ebitda_as_defined || 0);
  const adjustments = (bootstrap?.adjustments || [])
    .filter((a) => (includeAll ? true : a.status === 'approved'))
    .map((a) => {
      const annual = Object.values(a.impact_by_period || {}).reduce(
        (s, v) => s + Number(v || 0),
        0
      );
      let kind = 'neutral';
      if (annual > 0) kind = 'addback';
      else if (annual < 0) kind = 'cut';
      return {
        id: a.id,
        label: a.source_account_name,
        kind,
        delta: annual,
        category: a.category,
        reasoning: a.llm_reasoning,
        status: a.status,
      };
    })
    .filter((s) => s.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const steps = [];
  let running = start;
  steps.push({
    label: 'EBITDA, as defined',
    kind: 'start',
    base: 0,
    value: start,
    cumulative: start,
  });
  for (const adj of adjustments) {
    const next = running + adj.delta;
    if (adj.delta >= 0) {
      steps.push({
        label: adj.label,
        kind: adj.kind,
        base: running,
        value: adj.delta,
        cumulative: next,
        adjustmentId: adj.id,
        reasoning: adj.reasoning,
        category: adj.category,
        status: adj.status,
      });
    } else {
      // Cut bar: float starts at next, height is |delta|.
      steps.push({
        label: adj.label,
        kind: adj.kind,
        base: next,
        value: -adj.delta,
        cumulative: next,
        adjustmentId: adj.id,
        reasoning: adj.reasoning,
        category: adj.category,
        status: adj.status,
      });
    }
    running = next;
  }
  steps.push({
    label: 'Diligence-Adjusted EBITDA',
    kind: 'end',
    base: 0,
    value: running,
    cumulative: running,
  });

  return steps;
}

const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// Strip a leading "H/O " prefix that's noise on every adjustment label.
const cleanName = (s) => s.replace(/^H\/O\s+/i, '');

// Split a pillar label into up to two balanced lines on a space boundary.
const wrapPillar = (s) => {
  if (s.length <= 14) return [s];
  const mid = Math.floor(s.length / 2);
  let split = s.lastIndexOf(' ', mid);
  if (split < 4) split = s.indexOf(' ', mid);
  if (split < 0) return [truncate(s, 16)];
  return [s.slice(0, split), s.slice(split + 1)];
};

const AxisTick = ({ x, y, payload }) => {
  const raw = String(payload?.value ?? '');
  const dashIdx = raw.indexOf(' - ');
  const hasCode = dashIdx > -1;
  const lines = hasCode
    ? [raw.slice(0, dashIdx), truncate(cleanName(raw.slice(dashIdx + 3)), 13)]
    : wrapPillar(raw);
  return (
    <g transform={`translate(${x},${y + 14})`}>
      <text
        textAnchor="middle"
        fill="var(--text)"
        fontSize={hasCode ? 11 : 10.5}
        fontWeight={600}
      >
        {lines[0]}
      </text>
      {lines[1] && (
        <text
          y={13}
          textAnchor="middle"
          fill="var(--text)"
          opacity={hasCode ? 0.6 : 1}
          fontSize={hasCode ? 9.5 : 10.5}
          fontWeight={hasCode ? 500 : 600}
        >
          {lines[1]}
        </text>
      )}
    </g>
  );
};

const formatAxisValue = (v) => {
  const n = Number(v);
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${Math.round(n / 1000)}k`;
};

const renderBarValueLabel = (steps) => (props) => {
  const { x, y, width, value, index } = props;
  const step = steps[index];
  if (!step || step.kind === 'start' || step.kind === 'end') return null;
  if (!value) return null;
  const sign = step.kind === 'cut' ? '−' : '+';
  const cx = Number(x) + Number(width) / 2;
  const cy = Number(y) - 6;
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      fill="var(--text)"
      opacity={0.75}
      fontSize={10}
      fontWeight={500}
    >
      {sign}
      {formatAxisValue(Math.abs(Number(value)))}
    </text>
  );
};

const WaterfallTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        padding: '10px 12px',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--text)',
        maxWidth: 280,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.label}</div>
      {d.kind === 'start' || d.kind === 'end' ? (
        <div>{fmtUSD(d.cumulative)}</div>
      ) : (
        <>
          <div>
            {d.kind === 'cut' ? '−' : '+'}
            {fmtUSD(Math.abs(d.value))} ({d.category})
          </div>
          <div style={{ opacity: 0.6, marginTop: 4 }}>Running: {fmtUSD(d.cumulative)}</div>
          {d.reasoning && <div style={{ opacity: 0.6, marginTop: 6 }}>{d.reasoning}</div>}
        </>
      )}
    </div>
  );
};

const EbitdaWaterfall = ({ bootstrap }) => {
  const [includeAll, setIncludeAll] = useState(false);
  const steps = useMemo(() => buildSteps(bootstrap, includeAll), [bootstrap, includeAll]);
  const hasAdjustments = steps.length > 2;

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>EBITDA Waterfall</h2>
        <span className="qoe-wb-section-meta">
          {hasAdjustments ? `${steps.length - 2} step${steps.length - 2 === 1 ? '' : 's'}` : 'no steps yet'}
        </span>
      </div>

      <div className="qoe-wb-waterfall-wrap">
        <div className="qoe-wb-waterfall-toolbar">
          <label>
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
            />{' '}
            Include all flagged (not just approved)
          </label>
        </div>

        {!hasAdjustments ? (
          <div className="qoe-wb-waterfall-empty">
            No adjustments yet. {includeAll ? 'No flagged adjustments produce a non-zero impact.' : 'Approve a row in the Adjustments Workbench below to see it here.'}
          </div>
        ) : (
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              <BarChart data={steps} margin={{ top: 24, right: 32, left: 8, bottom: 16 }}>
                <CartesianGrid
                  vertical={false}
                  stroke="var(--border)"
                  strokeDasharray="0"
                  opacity={0.55}
                />
                <XAxis
                  dataKey="label"
                  interval={0}
                  height={48}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--border)', opacity: 0.6 }}
                  tick={<AxisTick />}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text)', opacity: 0.7 }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={formatAxisValue}
                />
                <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
                <Bar
                  dataKey="value"
                  stackId="wf"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={56}
                  isAnimationActive={false}
                >
                  {steps.map((s, i) => (
                    <Cell key={i} fill={COLOUR[s.kind] || COLOUR.neutral} />
                  ))}
                  <LabelList dataKey="value" content={renderBarValueLabel(steps)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default EbitdaWaterfall;
